import { Box, Button, Stack } from "@mui/material"
import { useParams } from "react-router-dom"
import { Bound } from "lib/@components/binding/Bound"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import { useRefresh } from "lib/@hooks/useRefresh"
import { getScheduleResolutions } from "./controller/get-schedule-resolutions.controller"
import { saveResolutionDecisions } from "./controller/save-resolution-decisions.controller"
import { applyScheduleResolutions } from "./controller/apply-schedule-resolutions.controller"
import { rebuildScheduleResolution } from "./controller/rebuild-resolution.controller"
import { saveChangeCriticality } from "./controller/save-change-criticality.controller"
import { ResolutionContent } from "./resolution-content"
import { ResolutionPreviewDialog } from "./resolution-preview-dialog"
import { useDialog } from "lib/@hooks/useDialog"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { navigate } from "lib/routes/navigate"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { NoResults } from "lib/@components/no-results/no-results"
import { useDemand } from "lib/authorization/use-demand"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { TreeToolbar } from "slot-definitions"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { getLegislationResolutionsRootPath } from "./legislation-resolution-route-paths"

export function ResolutionDetailContent() {
    const { id } = useParams()
    const canManage = useDemand("user")
    const resolution = getScheduleResolutions.useResults({ id }, withInitialValue(IS_LOADING))
    const refresh = useRefresh()
    const openPreview = useDialog(<ResolutionPreviewDialog />, { maxWidth: "lg", fullWidth: true })

    if (!canManage) return null
    if (resolution === IS_LOADING) return <LoadingScreen />
    if (!resolution) return <NoResults message="Resolution record not found." />

    const changes = resolution?.proposedChanges ?? []
    const allDecided = changes.length > 0 && changes.every((c) => c.decision !== null)
    const acceptedCount = changes.filter((c) => c.decision === "accept").length

    return (
        <PageBreadcrumbContainer
            title={resolution ? `${resolution.code} — Legislation Resolutions` : "Legislation Resolutions"}
            parentCrumbs={[{ label: "Legislation Resolutions", to: getLegislationResolutionsRootPath() }]}
        >
            <TreeToolbar.Plug>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handlePreview}
                        disabled={!resolution || changes.length === 0}
                    >
                        Preview Schedule
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRebuild}
                        disabled={resolution?.status === "processing"}
                    >
                        Recalculate
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        disabled={!allDecided || acceptedCount === 0 || resolution?.status === "applied"}
                        onClick={handleApply}
                    >
                        Apply {acceptedCount} Accepted Change{acceptedCount !== 1 ? "s" : ""}
                    </Button>
                </Stack>
            </TreeToolbar.Plug>
            <Box flex={1} sx={{ overflowY: "auto", py: 2, px: 1 }}>
                <Bound
                    target={resolution}
                    onDecisionChange={handleDecisionChange}
                    onTextChange={handleTextChange}
                    onProposedTaskChange={handleProposedTaskChange}
                    onCriticalityChange={handleCriticalityChange}
                    onAcceptAll={handleAcceptAll}
                >
                    <If truthy={resolution}>
                        <ResolutionContent />
                        <Else>
                            <NoResults message="Resolution record not found." />
                        </Else>
                    </If>
                </Bound>
            </Box>
        </PageBreadcrumbContainer>
    )

    async function handleDecisionChange(changeId, decision) {
        await saveResolutionDecisions({ id, decisions: [{ changeId, decision }] })
        refresh()
    }

    async function handleTextChange(changeId, editedText) {
        await saveResolutionDecisions({ id, decisions: [{ changeId, editedText }] })
        refresh()
    }

    async function handleProposedTaskChange(changeId, proposedTask) {
        await saveResolutionDecisions({ id, decisions: [{ changeId, proposedTask }] })
        refresh()
    }

    async function handlePreview() {
        await openPreview({ resolution })
    }

    async function handleRebuild() {
        const ok = await confirm(
            `Recalculate resolutions for "${resolution.code} — ${resolution.title}"? This will discard any existing proposals and decisions.`,
            "Recalculate"
        )
        if (!ok) return
        try {
            await rebuildScheduleResolution({ id })
            successSnackbar("Recalculation queued — results will appear shortly")
        } catch (e) {
            errorSnackbar(`Failed to queue recalculation: ${e.message}`)
        }
    }

    async function handleCriticalityChange(changeId, criticality) {
        await saveChangeCriticality({ id, changeId, criticality })
        refresh()
    }

    async function handleAcceptAll(decision) {
        const decisions = changes.map((c) => ({ changeId: c.id, decision }))
        await saveResolutionDecisions({ id, decisions })
        refresh()
    }

    async function handleApply() {
        const ok = await confirm(
            `Apply ${acceptedCount} accepted change${acceptedCount !== 1 ? "s" : ""} to schedule "${resolution.code} — ${resolution.title}"? This cannot be undone.`,
            "Apply Changes"
        )
        if (!ok) return
        try {
            await applyScheduleResolutions({ id })
            successSnackbar(`${acceptedCount} change${acceptedCount !== 1 ? "s" : ""} applied successfully`)
            navigate(getLegislationResolutionsRootPath())
        } catch (e) {
            errorSnackbar(`Failed to apply changes: ${e.message}`)
        }
    }
}
